// Types mirror escapemap/common/contracts.py (docs/DESIGN.md §3). Keep them in sync by hand;
// the pipeline validates the JSON, the frontend trusts it.

export type DimName = "month";

export interface DimSpec {
  labels: string[];
  annual: "mean" | "sum" | "none";
}

export interface ScaleSpec {
  scale: "diverging" | "sequential";
  palette: string;
  domain?: [number, number] | null;
}

/** How a pairwise matrix file is byte-encoded (escapemap.common.contracts.PairwiseEncoding). */
export interface PairwiseEncoding {
  dtype: "uint8";
  /** value = byte * scale (e.g. 2 -> minutes in 2-minute bins). */
  scale: number;
  /** byte meaning "beyond the cap". */
  beyond: number;
  /** byte meaning "no value" (no zone / no connection). */
  missing: number;
}

export interface RegistryMetric {
  id: string;
  /** "plz": one value per PLZ (default). "pairwise": value per (reference, plz). */
  kind?: "plz" | "pairwise";
  label: string;
  short: string;
  unit: string;
  decimals: number;
  dims: Partial<Record<DimName, DimSpec>>;
  /** Difference-vs-reference colouring; null for pairwise metrics (a diff of travel times
   * against the reference is meaningless — the reference is 0 by construction). */
  diff: ScaleSpec | null;
  abs: ScaleSpec;
  /** plz: path of a MetricFile JSON. pairwise: path of the binary uint8 matrix. */
  file: string;
  /** pairwise only: path of the PairwiseMeta JSON. */
  meta_file?: string | null;
  /** pairwise only. */
  encoding?: PairwiseEncoding | null;
  /** pairwise only: thresholds for reach bands, in the metric's unit (minutes), strictly increasing. */
  bands?: number[] | null;
  /** pairwise only: range of the "Reachable within" slider; without it the UI falls back to one button per band. */
  band_range?: { min: number; max: number; step: number } | null;
  source: string;
  method_url?: string | null;
  layer?: string;
}

/** escapemap.common.contracts.PairwiseMeta: describes a row-major N x N uint8 matrix keyed by
 * `plz_order` on both axes. value(ref, plz) = byte[row(ref) * n + col(plz)] * encoding.scale,
 * unless the byte equals encoding.beyond or encoding.missing. The diagonal is 0. */
export interface PairwiseMeta {
  metric: string;
  kind: "pairwise";
  unit: string;
  n: number;
  plz_order: string[];
  encoding: PairwiseEncoding;
  file: string;
  generated: string;
  source: string;
  source_version: string;
  definition: string;
  meta: Record<string, unknown>;
}

export interface Registry {
  version: number;
  generated?: string | null;
  metrics: RegistryMetric[];
}

export interface MetricFile {
  metric: string;
  dims: DimName[];
  shape: number[];
  generated: string;
  source: string;
  source_version: string;
  period?: [number, number] | null;
  values: Record<string, (number | null)[]>;
  meta: Record<string, unknown>;
}

export interface PlzRecord {
  name: string;
  names: string[];
  gemeinde: string;
  bfs: number;
  canton: string;
  lang: string;
  ref_e: number;
  ref_n: number;
  ref_lat: number;
  ref_lon: number;
  elev_ref: number;
  elev_mean: number;
  elev_min: number;
  elev_max: number;
  area_km2: number;
  parts: number;
}

export type PlzIndex = Record<string, PlzRecord>;

/** GeoJSON shapes we consume; only the properties we read are typed. */
export interface PlzFeatureProperties {
  plz: string;
  name: string;
}

export interface PlzFeatureCollection {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    id?: number | string;
    properties: PlzFeatureProperties;
    geometry: unknown;
  }>;
}

export interface OutlineFeatureCollection {
  type: "FeatureCollection";
  features: Array<{ type: "Feature"; properties: { canton: string }; geometry: unknown }>;
}

export interface DivergingPalette {
  neg: string;
  mid: string;
  pos: string;
}

export interface Palettes {
  diverging: Record<string, DivergingPalette>;
  sequential: Record<string, string[]>;
}

/** All data lives under ./data/ relative to index.html; never use a leading slash. */
export const DATA_BASE = "./data/";

async function getJson<T>(rel: string): Promise<T> {
  const resp = await fetch(DATA_BASE + rel);
  if (!resp.ok) throw new Error(`fetch ${rel}: HTTP ${resp.status}`);
  return (await resp.json()) as T;
}

export const loadRegistry = () => getJson<Registry>("registry.json");
export const loadPlzIndex = () => getJson<PlzIndex>("plz_index.json");
export const loadMetric = (m: RegistryMetric) => getJson<MetricFile>(m.file);
export const loadPalettes = () => getJson<Palettes>("palettes.json");
export const loadPlzGeoJson = () => getJson<PlzFeatureCollection>("plz.geojson");
export const loadOutline = () => getJson<OutlineFeatureCollection>("outline.geojson");

/**
 * HEAD-request a relative data path; true only on an HTTP 200 that isn't Vite's SPA-fallback
 * `index.html` for an unmatched route (both `vite dev` and `vite preview` return 200 text/html
 * for any missing path — verified empirically — whereas a real static host, e.g. GitHub Pages,
 * returns a true 404). A genuine pmtiles response is binary, never text/html. Never throws.
 */
export async function dataFileExists(rel: string): Promise<boolean> {
  try {
    const resp = await fetch(DATA_BASE + rel, { method: "HEAD" });
    if (!resp.ok) return false;
    const contentType = resp.headers.get("content-type") ?? "";
    return !contentType.includes("text/html");
  } catch {
    return false;
  }
}
